import { Typography } from 'antd'
import Image from 'next/legacy/image'
import styled from 'styled-components'

import { findByType } from './swmsTemplateHelper'

import { useLocale } from '@/context/localeContext'
import { TimezonedDate } from '@/context/timezoneContext'
import { TemplateRendererView } from '@/modules/templates/templateRenderer/TemplateRenderer'
import { Section, TemplateData } from '@/modules/templates/types/blueprint'

import BuildPassLogo from 'public/buildpass.svg'
import boots from 'public/ppe-icons/boots.svg'
import dustMask from 'public/ppe-icons/dust-mask.svg'
import earMuffs from 'public/ppe-icons/ear-muffs.svg'
import goggles from 'public/ppe-icons/goggles.svg'
import hardHat from 'public/ppe-icons/hardhat.svg'
import vest from 'public/ppe-icons/vest.svg'

import { Resource } from 'types/api.graphql'

const PADDING = '2rem'

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colour.neutral.white};
  min-height: 100vh;
`

const Content = styled.div`
  padding: 1rem ${PADDING} ${PADDING};
`

const Header = styled(Content)`
  background-color: ${({ theme }) => theme.colour.secondary._50};
`

const Icons = styled.div`
  display: flex;
  gap: 0.125rem;
  justify-content: flex-end;
`

const Icon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SwmsText = styled(Typography.Text).attrs({ type: 'secondary' })`
  display: flex;
  justify-content: flex-end;
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 2px;
  margin-top: 0.25rem;
  text-align: right;
  text-transform: uppercase;

  ${({ theme }) => theme.media.md`
    font-size: 0.875rem;
  `}
`

const STRIPE_HEIGHT = '6px'

type SectionTitleProps = {
  pageBreak: boolean
}

const SectionTitle = styled.div<SectionTitleProps>`
  position: relative;
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 4rem;
  display: flex;
  align-items: flex-end;

  page-break-before: ${({ pageBreak }) => (pageBreak ? 'always' : 'auto')};

  & > span {
    line-height: 1;
  }
`

const SectionSubtitle = styled(Typography.Text).attrs({ type: 'secondary' })`
  margin-top: 1.5rem;
  display: block;
`

const Stripes = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: -${PADDING};
  right: -${PADDING};
  height: ${STRIPE_HEIGHT};
  background-image: repeating-linear-gradient(
    -45deg,
    white,
    white 10px,
    ${({ theme }) => theme.colour.secondary._50} 10px,
    ${({ theme }) => theme.colour.secondary._50} 20px
  );

  @media print {
    left: 0;
    right: 0;
    background-image: none;
    background-color: ${({ theme }) => theme.colour.secondary._50};
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 1rem;
`

const ICON_SIZE = 20

const makeIcon = (src: string, alt: string) => ({
  label: alt,
  icon: (
    <Image width={ICON_SIZE} height={ICON_SIZE} src={src} alt={alt} priority />
  ),
})

const PPEList = (t: ReturnType<typeof useLocale>['t']) => [
  makeIcon(hardHat.src, t('swmsBuilder:ppe.hardHat')),
  makeIcon(earMuffs.src, t('swmsBuilder:ppe.hearingProtection')),
  makeIcon(goggles.src, t('swmsBuilder:ppe.eyeProtection')),
  makeIcon(dustMask.src, t('swmsBuilder:ppe.dustMask')),
  makeIcon(vest.src, t('swmsBuilder:ppe.hiVisVest')),
  makeIcon(boots.src, t('swmsBuilder:ppe.steelCappedBoots')),
]

type CustomSectionProps = {
  section: Section
  index: number
  children?: React.ReactNode
}

const CustomSection: React.FC<CustomSectionProps> = ({
  section,
  index,
  children,
}) => (
  <>
    <SectionTitle pageBreak={index > 0}>
      <Stripes />
      <Typography.Text>{section.name}</Typography.Text>
    </SectionTitle>

    {section.description && (
      <SectionSubtitle type="secondary">{section.description}</SectionSubtitle>
    )}

    <br />

    {children}
  </>
)

type Props = {
  swmsTemplate: TemplateData
  projectId: string
}

export const SwmsTemplateRenderer: React.FC<Props> = ({
  swmsTemplate,
  projectId,
}) => {
  const { t } = useLocale()
  const title = findByType('swmsGeneralInfo', swmsTemplate)?.[0]?.title

  return (
    <Wrapper>
      <Header>
        <Icons>
          {PPEList(t).map(({ label, icon }) => (
            <Icon key={label}>{icon}</Icon>
          ))}
        </Icons>

        <SwmsText>{t('swmsBuilder:labels.safeWorkMethodStatement')}</SwmsText>

        <Typography.Title level={2}>
          {t('swmsBuilder:labels.swmsTitle', { title })}
        </Typography.Title>
        <Typography.Text>
          <TimezonedDate date={new Date()} includeTime={false} />
        </Typography.Text>
      </Header>

      <Content>
        <TemplateRendererView
          resource={Resource.SwmsDoc}
          projectId={projectId}
          templateResponse={{ id: 'swms-builder', responseData: swmsTemplate }}
          renderOptions={{
            blockGroupBordered: false,
            blockGroupBackground: 'rgba(0,0,0,0)',
            preventPageBreakInsideBlock: false,
            renderingInPdf: true,
            sectionRenderer: CustomSection,
            pdfAppendix: true,
          }}
        />

        <Footer>
          {t('swmsBuilder:labels.poweredBy')}&nbsp;
          <Image
            src={BuildPassLogo.src}
            alt={t('swmsBuilder:labels.buildPassLogo')}
            width={178}
            height={30}
            objectFit="contain"
            priority
          />
        </Footer>
      </Content>
    </Wrapper>
  )
}
